/**
 * Wishlist Client
 * ───────────────
 * Manages wishlist state via App Proxy → Render → Shopify Admin API.
 * Supports two modes:
 *   - Logged-in: syncs with server, uses localStorage as cache
 *   - Guest: stores everything in localStorage (sl_wishlist_guest)
 *
 * Global: window.slWishlist = { sync, has, getAll, count, add, remove, toggle,
 *   onChange, isLoggedIn, isGuest, clearCache, fetchDetails, getGuestItems, clearGuestItems }
 */
(function() {
'use strict';

const PROXY_URL = '/apps/wishlist/api/wishlist';
const CACHE_KEY = 'sl_wishlist';
const CACHE_TS_KEY = 'sl_wishlist_ts';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const GUEST_KEY = 'sl_wishlist_guest';

/** @type {string[]} */
let items = [];
let synced = false;
let syncPromise = null;

/** @type {Array<{gid: string, title?: string, handle?: string, image?: string, price?: string, compareAtPrice?: string, addedAt?: string}>} */
let guestDetails = [];

// Event bus for UI updates
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn(items));
}

/** Subscribe to wishlist changes. Returns unsubscribe function. */
function onChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// --- localStorage cache (logged-in mode) ---

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      items = JSON.parse(raw);
      return true;
    }
  } catch { /* ignore */ }
  return false;
}

function saveCache() {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(items));
    localStorage.setItem(CACHE_TS_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

function isCacheFresh() {
  try {
    const ts = parseInt(localStorage.getItem(CACHE_TS_KEY) || '0', 10);
    return Date.now() - ts < CACHE_TTL;
  } catch { return false; }
}

function clearCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TS_KEY);
    localStorage.removeItem(GUEST_KEY);
  } catch { /* ignore */ }
}

// --- Guest localStorage ---

function loadGuestData() {
  try {
    const raw = localStorage.getItem(GUEST_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        guestDetails = parsed;
        items = guestDetails.map((entry) =>
          typeof entry === 'string' ? entry : entry.gid
        ).filter(Boolean);
        return true;
      }
    }
  } catch { /* ignore */ }
  guestDetails = [];
  return false;
}

function saveGuestData() {
  try {
    localStorage.setItem(GUEST_KEY, JSON.stringify(guestDetails));
  } catch { /* ignore */ }
}

// --- API calls (logged-in mode only) ---

async function apiCall(action, productId) {
  const body = { action };
  if (productId) body.productId = productId;

  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Wishlist API ${response.status}: ${text}`);
  }

  const data = await response.json();
  return data.wishlist || [];
}

// --- Public API ---

function isLoggedIn() {
  return window.slWishlistCustomerId != null;
}

function isGuest() {
  return !isLoggedIn();
}

/** Sync with server (logged-in) or load from localStorage (guest). */
function sync() {
  if (isGuest()) {
    loadGuestData();
    synced = true;
    notify();
    return Promise.resolve();
  }

  if (syncPromise) return syncPromise;

  // Load cache immediately for instant UI
  loadCache();

  // If cache is fresh, skip server sync
  if (isCacheFresh()) {
    synced = true;
    return Promise.resolve();
  }

  syncPromise = apiCall('list')
    .then((serverItems) => {
      items = serverItems;
      saveCache();
      synced = true;
      notify();
    })
    .catch((err) => {
      console.warn('Wishlist sync failed:', err.message);
      synced = true; // fall back to cache
    })
    .finally(() => {
      syncPromise = null;
    });

  return syncPromise;
}

function has(productGid) {
  return items.includes(productGid);
}

function getAll() {
  return [...items];
}

function count() {
  return items.length;
}

/**
 * Add a product to the wishlist.
 * @param {string} productGid
 * @param {object} [details] - Optional product details (used in guest mode)
 * @param {string} [details.title]
 * @param {string} [details.handle]
 * @param {string} [details.image]
 * @param {string} [details.price]
 * @param {string} [details.compareAtPrice]
 */
async function add(productGid, details) {
  if (isGuest()) {
    if (!items.includes(productGid)) {
      items.push(productGid);
      const entry = details
        ? { gid: productGid, ...details, addedAt: new Date().toISOString() }
        : { gid: productGid, addedAt: new Date().toISOString() };
      guestDetails.push(entry);
      saveGuestData();
      notify();
    }
    return;
  }

  // Logged-in: optimistic update, then API call
  if (!items.includes(productGid)) {
    items.push(productGid);
    saveCache();
    notify();
  }

  try {
    const serverItems = await apiCall('add', productGid);
    items = serverItems;
    saveCache();
    notify();
  } catch (err) {
    // Revert on failure
    items = items.filter((id) => id !== productGid);
    saveCache();
    notify();
    console.error('Wishlist add failed:', err.message);
  }
}

async function remove(productGid) {
  if (isGuest()) {
    items = items.filter((id) => id !== productGid);
    guestDetails = guestDetails.filter((entry) =>
      (typeof entry === 'string' ? entry : entry.gid) !== productGid
    );
    saveGuestData();
    notify();
    return;
  }

  // Logged-in: optimistic update, then API call
  const prev = [...items];
  items = items.filter((id) => id !== productGid);
  saveCache();
  notify();

  try {
    const serverItems = await apiCall('remove', productGid);
    items = serverItems;
    saveCache();
    notify();
  } catch (err) {
    // Revert on failure
    items = prev;
    saveCache();
    notify();
    console.error('Wishlist remove failed:', err.message);
  }
}

async function toggle(productGid, details) {
  if (has(productGid)) {
    await remove(productGid);
  } else {
    await add(productGid, details);
  }
}

async function fetchDetails() {
  if (isGuest()) {
    // Return guest details in the same format as the API response
    return guestDetails
      .filter((entry) => typeof entry !== 'string' && entry.gid)
      .map((entry) => ({
        gid: entry.gid,
        productGid: entry.gid,
        title: entry.title || '',
        handle: entry.handle || '',
        image: entry.image || null,
        price: entry.price ? Math.round(parseFloat(entry.price) * 100) : 0,
        compareAtPrice: entry.compareAtPrice && parseFloat(entry.compareAtPrice) > 0
          ? Math.round(parseFloat(entry.compareAtPrice) * 100)
          : null,
        available: true,
        variantId: null,
      }));
  }

  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'details' }),
  });

  if (!response.ok) {
    throw new Error(`Wishlist API ${response.status}`);
  }

  const data = await response.json();
  // Update local items from server response
  if (data.wishlist) {
    items = data.wishlist;
    saveCache();
    notify();
  }
  return data.products || [];
}

/** Returns full guest detail objects from localStorage. */
function getGuestItems() {
  return [...guestDetails];
}

/** Clears guest wishlist data from localStorage and memory. */
function clearGuestItems() {
  guestDetails = [];
  items = [];
  try {
    localStorage.removeItem(GUEST_KEY);
  } catch { /* ignore */ }
  notify();
}

// --- Init ---
// Load cache immediately so UI can render before sync completes
if (isLoggedIn()) {
  loadCache();
} else {
  loadGuestData();
}

const wishlist = {
  sync,
  has,
  getAll,
  count,
  add,
  remove,
  toggle,
  onChange,
  isLoggedIn,
  isGuest,
  clearCache,
  fetchDetails,
  getGuestItems,
  clearGuestItems,
};

// Expose globally
window.slWishlist = wishlist;

})();
